import React from 'react';
import { Table } from 'antd';
import { SmallDashOutlined } from '@ant-design/icons';
import {useScreenHeight} from "../../../../hooks/useScreen";

/**
 * AggregatedTickerTable is a component that displays a table of aggregated ticker data.
 * @param detailedRows - A 2D array of ticker data. The first row should contain column headers.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const AggregatedTickerTable = ({ detailedRows }) => {
  // Assuming the first row of detailedRows contains column headers
  const screenHeight = useScreenHeight();
  const columns = detailedRows[0].map((header, index) => ({
    title: header,
    dataIndex: index.toString(), // Ensure dataIndex is a string for Ant Design's requirements
    key: index.toString(),
    align: index === 0 ? 'left' : 'right', // Align headers and cell content
    sorter: (a, b) => a[index] - b[index],
    sortDirections: ['descend', 'ascend'],
    render: (text, record, rowIndex) => {
      if (index === 0) { // First column for row index
        return `${rowIndex + 1})  ${detailedRows[rowIndex+1][0].toUpperCase()}`; // Display row number starting from 1
      } else { // Handle percentage columns
        // Direct comparison of `text` with the previous row's value is not possible here because `dataSource` hasn't been fully constructed yet.
        // Instead, we check for the presence of a React component (EllipsisOutlined) or a number to decide rendering.
        if (React.isValidElement(text)) {
          return text; // If text is already a React component (EllipsisOutlined), return it directly.
        }
        // Format the number as a percentage to two decimal places.
        const formattedPercentage = `${parseFloat(text * 100).toFixed(2)}%`;
        return formattedPercentage;
      }
    },
  }));

  // Initialize an empty array to track the previous values for each column
  let prevValues = Array(detailedRows[0].length).fill(null);

  // Transform detailedRows into dataSource format expected by Ant Design Table
  const dataSource = detailedRows.slice(1).map((row, index) => {
    const rowObj = row.reduce((acc, cell, cellIndex) => {
      // Handling for percentage columns
      if (cellIndex > 1) {
        // Check if this cell's value is identical to the previous cell in the same column
        if (prevValues[cellIndex] === cell) {
          // Store a React component to indicate the value is identical (useful for the render method)
          acc[cellIndex] = <SmallDashOutlined style={{ color: 'gray' }} />;
        } else {
          // Otherwise, store the actual value
          acc[cellIndex] = cell;
        }
        // Update the tracked previous value for this column
        prevValues[cellIndex] = cell;
      } else {
        // For the first column, just store the value (e.g., the row index or identifier)
        acc[cellIndex] = cell;
      }
      return acc;
    }, { key: index });
    return rowObj;
  });

  // Reset prevValues for reuse in rendering logic
  prevValues = Array(detailedRows[0].length).fill(null);

  return (
    <Table
        className={'mid bold'}
        scroll={{ x: 'max-content', y: screenHeight - 300 }}
        columns={columns}
        dataSource={dataSource}
        pagination={{ pageSize: 250 }}

    />
  );
};

export default AggregatedTickerTable;
